/**
 * Custom-HTML block storage helper.
 *
 * Stored outside layout_blocks JSON (merchants can't inject HTML via the
 * flow definition). Matches the Connected-mode contract where the backend
 * keeps html in `checkout_html_blocks` and serves it through a sandbox
 * iframe endpoint. In Studio mode we store in localStorage under a
 * dedicated key, sanitize on read, and render via <iframe srcDoc>.
 *
 * Sanitizes with DOMPurify using the allowlist documented in
 * FLOWS.md § 11.9 C3.
 */

import DOMPurify from "dompurify";

const KEY_PREFIX = "costplus:studio:v1:html:";

const SANITIZE_CONFIG = {
  FORBID_TAGS: ["script", "iframe", "object", "embed", "style", "form"] as string[],
  FORBID_ATTR: [
    "onclick",
    "onload",
    "onerror",
    "onmouseover",
    "onfocus",
    "onblur",
    "onchange",
    "onsubmit",
    "formaction",
    "action",
  ] as string[],
  ALLOWED_URI_REGEXP:
    /^(?:(?:https?|mailto):|data:image\/(png|jpeg|gif|webp);base64,)/i,
  RETURN_TRUSTED_TYPE: false,
};

export function saveCustomHtml(id: string, rawHtml: string): string {
  // DOMPurify.sanitize returns TrustedHTML | string depending on config; we
  // disable TrustedHTML return above, so String() coerces for safety.
  const clean = String(DOMPurify.sanitize(rawHtml, SANITIZE_CONFIG));
  localStorage.setItem(KEY_PREFIX + id, clean);
  return clean;
}

export function loadCustomHtml(id: string): string {
  return localStorage.getItem(KEY_PREFIX + id) ?? "";
}

export function deleteCustomHtml(id: string): void {
  localStorage.removeItem(KEY_PREFIX + id);
}
